import { RentalConditions } from './RentalConditions';
import { User } from './User';
import { SharedDocumentsResponse, UserDocumentCategory } from './UserDocument';
import { Listing, ListingCancellationPolicy } from './Listing';
import { UnitType } from './inventory/UnitType';

interface Tenancy {
  uuid: string;
}

export interface Conversation {
  // The default response without expansions
  id: number;
  listingId: number;
  tenantId: number;
  advertiserId: number;
  createdAt: string;
  stateChangedAt: string;
  state: ConversationState;
  stateLabel: string;
  messageCount: number;
  paymentMethodId: number;
  flagUnread: boolean;
  flagArchived: boolean;
  flagStarred: boolean;
  uuid: string;
  bookingEngagementUUID: string;
  rentalConditionsId: number;
  bookingLink: boolean;

  // With available expansions - stateHistory, messages, listing, advertiser, tenant
  stateHistory?: ConversationStateItem[];
  messages?: Message[];
  listing?: Listing;
  unitType?: UnitType;
  advertiser?: User;
  tenant?: User;
  tenancy?: Tenancy;
  rentalConditions?: RentalConditions;
  sharedDocuments?: SharedDocumentsResponse;

  /** @deprecated use `rentalConditions` */
  startDate: string;
  /** @deprecated use `rentalConditions` */
  endDate: string;
  /** @deprecated use `rentalConditions.pricingData` */
  price: number;
  /** @deprecated use `rentalConditions.costs` */
  deposit: number;
  /** @deprecated use `rentalConditions.tenantFee` */
  fee: number;
  /** @deprecated use `rentalConditions.baseTenantFee` */
  baseFee: number;
  /** @deprecated use `rentalConditions.bookingAmount` */
  total: number;
  /** @deprecated use `rentalConditions` */
  currency: string;
  /** @deprecated use `rentalConditions` */
  cancellationPolicy?: ListingCancellationPolicy;
}

export interface ConversationStateItem {
  createdAt: string;
  state: number;
  label: string;
}

export interface Message {
  id: number;
  authorId: number;
  createdAt: string;
  deletedByAdmin: boolean;
  document?: MessageDocument;
  isDeleted: boolean;
  isFromEmail: boolean;
  isRead: boolean;
  isSystem: boolean;
  recipientId: number;
  text: string;
  translatedText?: string;
  type: 'text' | 'document';
}

export interface MessageDocument {
  mimeType: string;
  filename: string;
  size: number;
  category: UserDocumentCategory;
  title: string;
  requestUrl: string;
  redirectUrl: string;
  isDeleted: boolean;
}

export interface LastMessage {
  userId: number;
  messageId: number;
  startDate: string;
  endDate: string;
  message: string;
}

export enum ConversationState {
  InitialMessageState = -1,
  ContactState = 0,
  RequestedState = 1,
  AcceptedState = 2,
  DeclinedState = 3,
  PaidState = 4,
  ExpiredState = 5,
  PaidOutState = 6,
  SpecialOfferState = 8,
  PaymentPendingState = 9,
  PaymentFailedState = 10,
  PreRequestedState = 11,
  ToBePaidState = 12,
  PaymentOngoingState = 13,
  CancelledState = 14,
  CancellationOngoing = 15,
  ChangeRequested = 17,
}

type ConversationStateLabels = { [K in ConversationState]: string };

export const conversationStateLabels: ConversationStateLabels = {
  [ConversationState.InitialMessageState]: 'initial message request',
  [ConversationState.ContactState]: 'contact request',
  [ConversationState.RequestedState]: 'booking request',
  [ConversationState.AcceptedState]: 'accepted',
  [ConversationState.DeclinedState]: 'declined',
  [ConversationState.PaidState]: 'paid',
  [ConversationState.ExpiredState]: 'expired',
  [ConversationState.PaidOutState]: 'advertiser paid',
  [ConversationState.SpecialOfferState]: 'special offer',
  [ConversationState.PaymentPendingState]: 'payment pending',
  [ConversationState.PaymentFailedState]: 'payment failed',
  [ConversationState.PreRequestedState]: 'booking request - pay later',
  [ConversationState.ToBePaidState]: 'expecting payment',
  [ConversationState.PaymentOngoingState]: 'payment in progress',
  [ConversationState.CancelledState]: 'cancelled',
  [ConversationState.CancellationOngoing]: 'booking cancellation in progress',
  [ConversationState.ChangeRequested]:
    'change request - booking modification or cancellation',
};
